import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
const {FiLock,FiShield,FiEye,FiDatabase,FiUserCheck,FiAlertTriangle,FiBarChart2,FiCheckCircle} = FiIcons;

const Datenschutz = () => {
  return (
    <motion.div 
      initial={{opacity: 0}} 
      animate={{opacity: 1}} 
      exit={{opacity: 0}} 
      className="min-h-screen bg-gray-900"
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`
        }} />
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{y: 50, opacity: 0}} 
            animate={{y: 0, opacity: 1}} 
            transition={{duration: 0.8}} 
            className="text-center"
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
              <span className="gradient-text">Datenschutz</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
              Wie wir Ihre Daten schützen und verwenden
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Overview */}
            <motion.section 
              initial={{x: -50, opacity: 0}} 
              whileInView={{x: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8}}
              className="bg-gray-800 rounded-xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiEye} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Datenschutz auf einen Blick</h2>
              </div>
              
              <div className="space-y-4 text-gray-300">
                <p>
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, 
                  wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                </p>
                <p className="mt-2">
                  Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder eMail-Adressen) erhoben werden, 
                  erfolgt dies soweit möglich stets auf freiwilliger Basis. Die Nutzung der Angebote und Dienste ist, soweit möglich, 
                  stets ohne Angabe personenbezogener Daten möglich.
                </p>
              </div>
            </motion.section>

            {/* Data Collection */}
            <motion.section 
              initial={{x: 50, opacity: 0}} 
              whileInView={{x: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8, delay: 0.2}}
              className="bg-gray-800 rounded-xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiDatabase} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Datenerfassung auf unserer Website</h2>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
                  <p>
                    Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie 
                    dem Impressum dieser Website entnehmen.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Wie erfassen wir Ihre Daten?</h3>
                  <p>
                    Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um Daten 
                    handeln, die Sie in ein Kontaktformular eingeben.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Contact Form */}
            <motion.section 
              initial={{y: 50, opacity: 0}} 
              whileInView={{y: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8, delay: 0.4}}
              className="bg-gray-800 rounded-xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiLock} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Kontaktformular</h2>
              </div>
              
              <div className="space-y-4 text-gray-300">
                <p>
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
                  inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von 
                  Anschlussfragen bei uns gespeichert.
                </p>
                <p>
                  Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Die Durchführung einer vorvertraglichen Beziehung 
                  ist ohne Ihre freiwillige Angabe nicht möglich.
                </p>
              </div>
            </motion.section>

            {/* Spamschutz mit Google reCAPTCHA */}
            <motion.section 
              initial={{x: 50, opacity: 0}} 
              whileInView={{x: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8, delay: 0.5}}
              className="bg-gray-800 rounded-xl p-8 border border-gray-700"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Spamschutz mit Google reCAPTCHA</h2>
              </div>
              
              <div className="space-y-4 text-gray-300">
                <p>
                  Wir haben Google reCAPTCHA in Formularen auf unserer Webseite implementiert, um zu überprüfen, ob die in den Formularen eingegebenen Daten von menschlichen Besuchern stammen oder von Maschinen oder automatisierten Programmen, die auch als "Bots" bekannt sind. Dieses Tool analysiert automatisch das Verhalten der Webseitenbesucher, sobald sie mit der Webseite interagieren, unter Verwendung verschiedener Informationen wie IP-Adresse, Aufenthaltsdauer und Mausbewegungen.
                </p>
                <p>
                  Die verarbeiteten Daten umfassen Nutzungsdaten wie die aufgerufene Webseite, Datum und Uhrzeit des Zugriffs und Mausbewegungen sowie Kommunikationsdaten wie IP-Adressen, Browsertyp und Betriebssystem. Diese Verarbeitung richtet sich an Webseitenbesucher und Nutzer von Online-Diensten.
                </p>
                <p>
                  Der Hauptzweck dieser Verarbeitung besteht darin, den Missbrauch unseres Kontaktformulars zu verhindern und dadurch die Sicherheit zu erhöhen. Die rechtliche Grundlage dafür ist unser berechtigtes Interesse gemäß Art. 6 Abs. 1 S. 1 lit. f. DSGVO. Die Datenempfänger sind Google Ireland Ltd, ansässig in Google Building Gordon House, Barrow St, Dublin 4, Irland, und unser Webseiten-Hosting-Anbieter. Während Ihre persönlichen Daten innerhalb der EU verarbeitet werden, wird die Aufbewahrungsfrist für die verarbeiteten Daten von Google Ireland Limited bestimmt.
                </p>
                <p className="pt-2">
                  Weitere Informationen finden Sie in der Datenschutzerklärung von Google reCAPTCHA: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 ml-1">https://policies.google.com/privacy</a>
                </p>
              </div>
            </motion.section>

            {/* Other Third Party Services */}
            <motion.section 
              initial={{y: 50, opacity: 0}} 
              whileInView={{y: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8, delay: 0.6}}
              className="bg-gray-800 rounded-xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Weitere Drittanbieter-Dienste</h2>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Google Maps</h3>
                  <p>
                    Diese Website verwendet Google Maps zur Anzeige von Karten und Routenplanung. Durch die Nutzung von 
                    Google Maps werden Daten an Google übermittelt. Weitere Informationen finden Sie in der 
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 ml-1">Datenschutzerklärung von Google</a>.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">AnyChat</h3>
                  <p>
                    Für unser Kontaktformular nutzen wir AnyChat. Die Daten werden sicher übertragen und ausschließlich 
                    zur Bearbeitung Ihrer Anfrage verwendet. Weitere Informationen finden Sie in der 
                    <a href="https://anychat.one/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 ml-1">Datenschutzerklärung von AnyChat</a>.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Your Rights */}
            <motion.section 
              initial={{x: -50, opacity: 0}} 
              whileInView={{x: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8, delay: 0.7}}
              className="bg-gray-800 rounded-xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiUserCheck} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Ihre Rechte</h2>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Auskunftsrecht & Berichtigung</h3>
                  <p>
                    Sie haben jederzeit das Recht unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten 
                    personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung, Sperrung oder Löschung dieser Daten zu verlangen.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Widerspruchsrecht</h3>
                  <p>
                    Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die 
                    Verarbeitung Sie betreffender personenbezogener Daten, die gemäß Art. 6 Abs. 1 lit. f DSGVO zur Wahrung 
                    berechtigter Interessen verarbeitet werden, Widerspruch einzulegen.
                  </p>
                  <p className="mt-2">
                    Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten durch Dritte zur Übersendung von 
                    nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit ausdrücklich widersprochen.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Recht auf Datenübertragbarkeit</h3>
                  <p>
                    Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines 
                    Vertrags automatisiert verarbeiten, an sich oder an Dritte in einem gängigen, maschinenlesbaren Format 
                    aushändigen zu lassen.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Contact for Privacy */}
            <motion.section 
              initial={{x: 50, opacity: 0}} 
              whileInView={{x: 0, opacity: 1}} 
              viewport={{once: true}} 
              transition={{duration: 0.8, delay: 0.8}}
              className="bg-gray-800 rounded-xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-white">Datenschutzbeauftragter</h2>
              </div>
              
              <div className="space-y-4 text-gray-300">
                <p>
                  Bei Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte können Sie sich jederzeit an uns wenden:
                </p>
                <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                  <p><strong>Bavaria Custom Bikes</strong></p>
                  <p>Datenschutzbeauftragter</p>
                  <p>Ulmer Straße 56</p>
                  <p>86660 Tapfheim</p>
                  <p>E-Mail: info@bavaria-custom-bikes.com</p>
                </div>
                <p className="text-sm">
                  Wir werden Ihre Anfrage so schnell wie möglich bearbeiten und Ihnen innerhalb von 30 Tagen eine Rückmeldung geben.
                </p>
              </div>
            </motion.section>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Datenschutz;